// Step 4: BrandSubmission for brand signup wizard
// Simple success screen after form submission
// Creates Firebase user and shows confirmation

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext.jsx';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile, fetchSignInMethodsForEmail, deleteUser } from 'firebase/auth';
import { auth, db } from '../../../../../firebaseConfig.js';

import { validatePassword } from '../../../../utils/passwordValidation.js';
import { normalizePhone } from '../../../../utils/phoneValidation.js';
import { uploadUserImage } from '../../../../lib/uploadProfileImage.js';
import { incrementCreatorCount } from '../../../../utils/incrementCreatorCount.js';
import { triggerWelcomeEmail } from '../../../../lib/email/triggerWelcomeEmail';

const BrandSubmission = ({ data, onDataChange, onNext, onBack, onNavigate, user }) => {
  const { profile, user: authUser } = useAuth();
  
  // Editable copy constants
  const COPY = {
    title: 'Submit Application',
    subtitle: 'Complete your brand registration',
    submitButton: 'Submit for Approval'
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Extract data from wizard state (always use current data, not stale state)
  const brandData = data?.brand || {};
  const accountData = data?.account || {};

  // Check if user already has a complete profile - if so, redirect to pending approval or dashboard
  useEffect(() => {
    if (authUser && profile) {
      // If user is authenticated and has a complete profile, they've already submitted
      if (profile.onboardingComplete && profile.userType === 'brand') {
        console.log('✅ User already has complete brand profile - redirecting...');
        // Redirect based on approval status
        if (profile.status === 'pending_approval') {
          onNavigate('pending-approval');
        } else if (profile.status === 'approved') {
          onNavigate('dashboard');
        } else {
          // For any other status, go to pending approval page
          onNavigate('pending-approval');
        }
      }
    }
  }, [authUser, profile, onNavigate]);

  // Clear error when email changes (e.g., user goes back and changes email)
  // This allows user to retry with a new email after getting a duplicate email error
  useEffect(() => {
    if (error) {
      // Clear error when email changes - allows user to retry with new email
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountData.email]); // Re-run when email changes

  // Check if all required data is present
  const isComplete = 
    brandData.companyName && 
    brandData.companyWebsite &&
    brandData.description &&
    accountData.firstName && 
    accountData.lastName && 
    accountData.email && 
    accountData.phone &&
    accountData.password;

  // Debug logging
  console.log('🔍 BrandSubmission validation check:');
  console.log('📊 brandData:', brandData);
  console.log('👤 accountData:', accountData);
  console.log('✅ isComplete:', isComplete);

  // Validation function
  const validate = async (stepData) => {
    const errors = [];
    
    if (!isComplete) {
      errors.push('Please complete all required fields before submitting.');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  };

  // Attach validate function to component
  BrandSubmission.validate = validate;

  const handleSubmit = async () => {
    // Prevent double submission
    if (isSubmitting || isSubmitted) {
      console.log('⚠️ Submission already in progress or completed');
      return;
    }

    // Check if user already has a complete profile
    if (authUser && profile && profile.onboardingComplete && profile.userType === 'brand') {
      console.log('⚠️ User already has a complete profile - redirecting...');
      if (profile.status === 'pending_approval') {
        onNavigate('pending-approval');
      } else if (profile.status === 'approved') {
        onNavigate('dashboard');
      } else {
        onNavigate('pending-approval');
      }
      return;
    }

    // Validate all data
    const validation = await validate(data);
    if (!validation.valid) {
      setError(validation.errors.join(', '));
      return;
    }

    setIsSubmitting(true);
    setError(''); // Clear any previous errors

    // Add timeout to prevent infinite hanging
    const timeoutId = setTimeout(() => {
      console.error('⏰ Brand submission timed out after 30 seconds');
      setError('Submission is taking longer than expected. Please check your connection and try again.');
      setIsSubmitting(false);
    }, 30000);

    try {
      // Normalize email: trim whitespace and convert to lowercase
      // This ensures consistent checking and storage
      const normalizedEmail = (accountData.email || '').trim().toLowerCase();
      
      console.log('🚀 Starting brand signup process...');
      console.log('📧 Original email:', accountData.email);
      console.log('📧 Normalized email:', normalizedEmail);
      console.log('🔑 Password length:', accountData.password?.length);
      
      // Validate required fields before proceeding
      if (!normalizedEmail || !accountData.password || !accountData.firstName || !accountData.lastName) {
        throw new Error('Missing required account information');
      }

      if (!brandData.companyName || !brandData.companyWebsite || !brandData.description) {
        throw new Error('Missing required brand information');
      }
      
      // Server-side password validation (prevent bypassing client validation)
      if (!accountData.password) {
        throw new Error('Password is required');
      }
      
      // Validate password with error handling
      try {
        const passwordValidation = validatePassword(accountData.password);
        if (!passwordValidation.valid) {
          throw new Error(passwordValidation.errors.join('. '));
        }
      } catch (passwordError) {
        console.error('Password validation error:', passwordError);
        throw new Error(passwordError.message || 'Password validation failed');
      }
      
      // Step 1: Check if email already exists and handle recovery OR create new Auth user
      // IMPORTANT: Always check using the CURRENT normalized email from form data
      console.log('⏳ Step 1: Checking if email already exists...');
      let currentUser = null;
      let isNewAuthUser = false; // Track if we're creating a brand new Auth user
      
      try {
        // Check if email is already registered - use normalized email
        const signInMethods = await fetchSignInMethodsForEmail(auth, normalizedEmail);
        
        if (signInMethods.length > 0) {
          // Email exists in Auth - check if Firestore profile exists
          console.log('⚠️ Email already exists in Auth, checking Firestore profile...');
          
          // Try to sign in to get the user - use normalized email
          try {
            const signInCredential = await signInWithEmailAndPassword(
              auth,
              normalizedEmail,
              accountData.password
            );
            currentUser = signInCredential.user;
            console.log('✅ Signed in to existing Auth account:', currentUser.uid);
            
            // Check if Firestore profile exists
            const userDocRef = doc(db, 'users', currentUser.uid);
            const userDoc = await getDoc(userDocRef);
            
            if (userDoc.exists()) {
              // Profile exists - can't complete signup
              const error = new Error('That email already has a complete account.');
              error.code = 'auth/email-already-in-use';
              throw error;
            } else {
              // Auth exists but no Firestore profile - this is recovery case
              console.log('⚠️ Auth user exists but no Firestore profile - completing profile creation...');
              isNewAuthUser = false; // This is recovery, not new
            }
          } catch (signInError) {
            if (signInError.code === 'auth/wrong-password') {
              const error = new Error('That email already has an account with a different password.');
              error.code = 'auth/email-already-in-use';
              throw error;
            }
            throw signInError;
          }
        } else {
          // Email doesn't exist - create new Auth user - use normalized email
          console.log('⏳ Creating new Firebase Auth user...');
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        normalizedEmail,
        accountData.password
      );
          currentUser = userCredential.user;
          isNewAuthUser = true; // Mark this as a new Auth user we created
      console.log('✅ Firebase Auth user created:', currentUser.uid);
        }
      } catch (checkError) {
        // Re-throw email-already-in-use errors
        if (checkError.code === 'auth/email-already-in-use' || checkError.message === 'auth/email-already-in-use' || checkError.message?.includes('already has an account')) {
          const error = new Error(checkError.message || 'That email already has an account.');
          error.code = 'auth/email-already-in-use';
          throw error;
        }
        throw checkError;
      }

      // Step 2: Update user profile with displayName
      console.log('⏳ Step 2: Updating user profile...');
      const displayName = `${accountData.firstName} ${accountData.lastName}`;
      await updateProfile(currentUser, {
        displayName: displayName
      });
      console.log('✅ User profile updated with displayName:', displayName);

      // Step 3: Upload logo if provided
      let logoUrl = null;
      if (data.logoFile) {
        console.log('📸 Step 3: Uploading company logo...');
        try {
          logoUrl = await uploadUserImage(data.logoFile, currentUser.uid, 'logo');
          console.log('✅ Company logo uploaded:', logoUrl);
        } catch (uploadError) {
          console.error('❌ Error uploading logo:', uploadError);
          // Don't block signup if upload fails - just log the error
          // User can upload later from Settings
        }
      } else if (brandData.logoUrl && brandData.logoUrl.startsWith('http')) {
        // Fallback to existing logoUrl if provided (for edge cases)
        logoUrl = brandData.logoUrl;
      }

      // Step 4: Create the complete brand profile
      console.log('⏳ Step 4: Preparing brand profile data...');
      const brandProfile = {
        // Basic info
        companyName: brandData.companyName,
        companyWebsite: brandData.companyWebsite,
        logoUrl: logoUrl || null,
        description: brandData.description, // Company Bio
        
        // Contact info - use normalized email for consistency
        email: normalizedEmail,
        phone: normalizePhone(accountData.phone), // Store normalized phone (digits only)
        contactName: `${accountData.firstName} ${accountData.lastName}`,
        firstName: accountData.firstName,
        lastName: accountData.lastName,
        
        // System fields
        uid: currentUser.uid,
        userType: 'brand',
        role: 'brand', // Also set role for consistency
        onboardingComplete: true,
        status: 'pending_approval',
        submittedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        emailSentAt: {},
        featureFirstUse: {},
        emailMeta: {},
      };

      // Step 5: Create user document in Firestore
      // If this fails, we can clean up the Auth user
      console.log('⏳ Step 5: Saving to Firestore (CRITICAL - must succeed)...');
      console.log('📝 Brand profile data:', JSON.stringify(brandProfile, null, 2));
      console.log('👤 Current user UID:', currentUser.uid);
      console.log('🔑 User authenticated:', !!currentUser);
      console.log('🔑 Auth token available:', !!auth.currentUser);
      
      try {
        // Attempt to save to Firestore - THIS MUST SUCCEED OR WE CLEAN UP
        await setDoc(doc(db, 'users', currentUser.uid), brandProfile);
        
        // Increment creator count (this is a new creator - either new Auth user or recovery case with new Firestore profile)
        await incrementCreatorCount();
        
        console.log('✅ Brand profile created in Firestore - signup will complete');
        console.log('[signup] Firestore user doc created (brand wizard)', { uid: currentUser.uid });
        await triggerWelcomeEmail(currentUser.uid);
      } catch (firestoreError) {
        console.error('❌ Firestore write FAILED:', firestoreError);
        console.error('Firestore error code:', firestoreError?.code);
        console.error('Firestore error message:', firestoreError?.message);
        
        // CRITICAL CLEANUP: If Firestore fails AND we created a new Auth user, DELETE the Auth user
        // This prevents orphaned accounts from blocking future signups
        if (isNewAuthUser && currentUser) {
          console.error('🧹 CLEANUP: Firestore failed but we created a new Auth user - deleting Auth user to prevent orphaned account...');
          try {
            await deleteUser(currentUser);
            console.log('✅ CLEANUP SUCCESS: Auth user deleted - user can retry signup');
          } catch (deleteError) {
            console.error('⚠️ CLEANUP FAILED: Could not delete Auth user (may need manual cleanup in Firebase Console):', deleteError);
            // Continue - at least we tried to clean up
          }
        } else {
          console.log('ℹ️ No cleanup needed - this was a recovery case (Auth user already existed)');
        }
        
        // Re-throw to be caught by outer catch block
        throw firestoreError;
      }

      // Step 6: Clear wizard draft data
      console.log('⏳ Step 6: Clearing localStorage...');
      localStorage.removeItem(`ih.wizardDraft.${currentUser.uid}`);
      localStorage.removeItem('ih.wizardDraft.preAuth');
      localStorage.removeItem('ih.selectedRole');
      console.log('✅ Local storage cleared');

      // Step 7: Clear timeout and show success state
      clearTimeout(timeoutId);
      console.log('🎉 Brand signup completed successfully!');
      
      // Set submitted state and redirect after a brief delay to show success
      setIsSubmitted(true);
      
      // Redirect to pending approval page after 2 seconds
      // This ensures the user sees the success message but doesn't get stuck
      setTimeout(() => {
        console.log('🔄 Redirecting to pending approval page...');
        onNavigate('pending-approval');
      }, 2000);

    } catch (error) {
      clearTimeout(timeoutId);
      console.error('❌ Brand submission error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      
      // More detailed error handling
      let errorMessage = 'Failed to create brand profile. Please try again.';
      
      if (error.code) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'That email already has an account. If you started signing up previously, try entering your password again to complete your profile. Otherwise, please use a different email or sign in.';
            break;
          case 'auth/weak-password':
            errorMessage = 'Password is too weak. Please choose a stronger password.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Please enter a valid email address.';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Network error. Please check your internet connection and try again.';
            break;
          case 'permission-denied':
            // Firestore permission denied - likely a security rules issue
            errorMessage = 'Permission denied: Unable to save profile. This may be a security rules issue. Please check Firebase Console > Firestore > Rules. Users should be able to create their own document in the users collection.';
            break;
          default:
            errorMessage = `Error (${error.code}): ${error.message || 'Unknown error'}. Please check the console for details or contact support.`;
        }
      } else if (error.message) {
        errorMessage = `Error: ${error.message}. Please try again.`;
      }
      
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };


  // Show success screen after submission OR if user already has complete profile
  if (isSubmitted || (authUser && profile && profile.onboardingComplete && profile.userType === 'brand')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50/30 p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-md p-8 text-center space-y-4">
            {/* Success Icon */}
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100">
              <span className="text-4xl">🎉</span>
            </div>

            {/* Success Message */}
            <h2 className="text-2xl font-bold text-gray-900">
              Your brand application has been submitted!
            </h2>
            
            <p className="text-gray-600">
              Please wait for approval. You'll receive an email once your brand profile is approved.
            </p>

            {/* Action Button */}
            <button
              onClick={() => {
                if (profile?.status === 'pending_approval') {
                  onNavigate('pending-approval');
                } else if (profile?.status === 'approved') {
                  onNavigate('dashboard');
                } else {
                  onNavigate('pending-approval');
                }
              }}
              className="w-full sm:w-auto px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              {profile?.status === 'approved' ? 'Go to Dashboard' : 'View Status'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show submit form if not submitted yet
  return (
    <div className="text-center py-12">
      {/* Submit Button */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Ready to Submit?
        </h2>
        <p className="text-gray-600 mb-8">
          Review your information and submit your brand application for approval.
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm max-w-md mx-auto">
          {error}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
        {/* Back Button */}
        <button
          onClick={onBack}
          disabled={isSubmitting}
          className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Back to Edit
        </button>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !isComplete}
          className="px-8 py-3 text-lg font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {isSubmitting ? 'Creating Profile...' : COPY.submitButton}
        </button>
      </div>

      {/* Incomplete Data Warning */}
      {!isComplete && (
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
          <p className="font-medium mb-2">Please complete all required fields:</p>
          <ul className="list-disc list-inside space-y-1">
            {!brandData.companyName && <li>Company Name</li>}
            {!brandData.companyWebsite && <li>Company Website</li>}
            {!brandData.description && <li>Company Bio</li>}
            {!accountData.firstName && <li>First Name</li>}
            {!accountData.lastName && <li>Last Name</li>}
            {!accountData.email && <li>Email Address</li>}
            {!accountData.phone && <li>Phone Number</li>}
            {!accountData.password && <li>Password</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BrandSubmission;